import { useState } from "react";
import { useApp } from "@/lib/store";
import { Map, Settings, ShoppingCart, Search, LogOut, Compass, X, Volume2, VolumeX } from "lucide-react";

const ROOM_ICON = {};

export function HUD({ vr, onExit }) {
  const { world, room, setRoom, setShowMap, setShowSettings, setShowCart, setInspect, catalog, track } = vr;
  const { cart } = useApp();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const roomName = (world.rooms || []).find((r) => r.key === room)?.name || "The Atrium";
  const count = cart.reduce((a, c) => a + c.qty, 0);

  const results = q.length > 1 ? catalog.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 6) : [];
  const goTo = (p) => { setRoom("gallery"); setTimeout(() => setInspect(p.slug), 350); setOpen(false); setQ(""); track("search_takeme", { product_slug: p.slug }); };

  return (
    <>
      <div className="fixed top-0 inset-x-0 z-[70] flex items-center gap-2 px-3 py-2.5 bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-2 bg-black/55 backdrop-blur rounded-full pl-3 pr-1 py-1 border border-white/10">
          <Compass size={15} className="text-cyan-300" />
          <span className="text-xs font-mono text-cyan-100 tracking-widest uppercase" data-testid="vr-room-name">{roomName}</span>
        </div>
        <div className="flex-1" />
        {/* search */}
        <div className="pointer-events-auto relative">
          <button onClick={() => setOpen((o) => !o)} data-testid="vr-search-btn" className="w-9 h-9 rounded-full bg-black/55 border border-white/10 flex items-center justify-center text-slate-200 hover:text-cyan-300"><Search size={16} /></button>
          {open && (
            <div className="absolute right-0 mt-2 w-72 bg-[#080c12] border border-white/10 rounded-2xl p-2 shadow-2xl">
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search peptides… (e.g. BPC-157)"
                data-testid="vr-search-input" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none" />
              <div className="mt-1 max-h-60 overflow-y-auto">
                {results.map((p) => (
                  <button key={p.slug} onClick={() => goTo(p)} data-testid={`vr-search-result-${p.slug}`}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-sm text-slate-200 flex justify-between">
                    <span>{p.name}</span><span className="text-cyan-300 text-xs font-mono">TAKE ME →</span>
                  </button>
                ))}
                {q.length > 1 && results.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No matches</div>}
              </div>
            </div>
          )}
        </div>
        <button onClick={() => setShowMap(true)} data-testid="vr-map-btn" className="pointer-events-auto w-9 h-9 rounded-full bg-black/55 border border-white/10 flex items-center justify-center text-slate-200 hover:text-cyan-300"><Map size={16} /></button>
        <button onClick={() => setShowSettings(true)} data-testid="vr-settings-btn" className="pointer-events-auto w-9 h-9 rounded-full bg-black/55 border border-white/10 flex items-center justify-center text-slate-200 hover:text-cyan-300"><Settings size={16} /></button>
        <button onClick={() => setShowCart(true)} data-testid="vr-cart-btn" className="pointer-events-auto relative w-9 h-9 rounded-full bg-black/55 border border-white/10 flex items-center justify-center text-slate-200 hover:text-cyan-300">
          <ShoppingCart size={16} />
          {count > 0 && <span className="absolute -top-1 -right-1 bg-cyan-400 text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{count}</span>}
        </button>
        <button onClick={onExit} data-testid="vr-exit-btn" className="pointer-events-auto w-9 h-9 rounded-full bg-black/55 border border-white/10 flex items-center justify-center text-rose-300 hover:text-rose-200"><LogOut size={16} /></button>
      </div>
      <div className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[65] text-[10px] font-mono text-slate-500 tracking-widest pointer-events-none hidden md:block">
        WASD / ARROWS TO MOVE · DRAG TO LOOK · CLICK OBJECTS TO INTERACT
      </div>
    </>
  );
}

export function MapModal({ vr }) {
  const { world, room, setRoom, setShowMap } = vr;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="vr-map-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowMap(false)} />
      <div className="relative w-full max-w-lg rounded-3xl bg-[#080c12] border border-cyan-400/20 p-6">
        <button onClick={() => setShowMap(false)} className="absolute top-3 right-3 p-2 text-slate-400 hover:text-white"><X size={18} /></button>
        <div className="text-cyan-300 font-mono text-xs tracking-widest">FAST TRAVEL</div>
        <h2 className="text-2xl font-semibold text-white mt-1 mb-4">CG Labz World Map</h2>
        <div className="grid grid-cols-2 gap-3">
          {(world.rooms || []).map((r) => (
            <button key={r.key} onClick={() => setRoom(r.key)} data-testid={`vr-map-${r.key}`}
              className={`text-left rounded-2xl p-4 border transition ${room === r.key ? "border-cyan-400 bg-cyan-500/10" : "border-white/10 hover:border-cyan-400/40 bg-white/[0.02]"}`}>
              <div className="text-sm font-semibold text-white">{r.name}</div>
              <div className="text-[11px] text-slate-400 mt-1 line-clamp-2">{r.description}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SettingsPanel({ vr }) {
  const { quality, setQuality, audioOn, setAudioOn, reducedMotion, setReduced, setShowSettings } = vr;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="vr-settings-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowSettings(false)} />
      <div className="relative w-full max-w-sm rounded-3xl bg-[#080c12] border border-white/10 p-6">
        <button onClick={() => setShowSettings(false)} className="absolute top-3 right-3 p-2 text-slate-400 hover:text-white"><X size={18} /></button>
        <h2 className="text-xl font-semibold text-white mb-4">Comfort & Performance</h2>
        <div className="text-[11px] font-mono text-slate-400 mb-2">GRAPHICS QUALITY</div>
        <div className="flex flex-wrap gap-2 mb-4">
          {["auto", "high", "medium", "low"].map((q) => (
            <button key={q} onClick={() => setQuality(q)} data-testid={`vr-quality-${q}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono uppercase border ${quality === q ? "bg-cyan-400 text-black border-cyan-400" : "border-white/15 text-slate-300"}`}>{q}</button>
          ))}
        </div>
        <button onClick={() => setAudioOn(!audioOn)} className="w-full flex items-center justify-between rounded-xl border border-white/10 px-4 py-3 mb-2 text-slate-200">
          <span className="text-sm flex items-center gap-2">{audioOn ? <Volume2 size={15} /> : <VolumeX size={15} />} Immersive audio</span>
          <span className={`text-xs font-mono ${audioOn ? "text-cyan-300" : "text-slate-500"}`}>{audioOn ? "ON" : "OFF"}</span>
        </button>
        <button onClick={() => setReduced(!reducedMotion)} className="w-full flex items-center justify-between rounded-xl border border-white/10 px-4 py-3 text-slate-200">
          <span className="text-sm">Reduced motion</span>
          <span className={`text-xs font-mono ${reducedMotion ? "text-cyan-300" : "text-slate-500"}`}>{reducedMotion ? "ON" : "OFF"}</span>
        </button>
      </div>
    </div>
  );
}

// Mobile virtual joystick → writes into shared input ref
export function Joystick({ input }) {
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const handle = (e) => {
    const t = e.touches ? e.touches[0] : e;
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
    let dx = (t.clientX - cx) / (rect.width / 2), dy = (t.clientY - cy) / (rect.height / 2);
    const mag = Math.hypot(dx, dy); if (mag > 1) { dx /= mag; dy /= mag; }
    setKnob({ x: dx * 30, y: dy * 30 });
    input.current.move = [dx, dy];
  };
  const reset = () => { setKnob({ x: 0, y: 0 }); input.current.move = [0, 0]; };
  return (
    <div className="fixed bottom-6 left-6 z-[65] md:hidden" data-testid="vr-joystick"
      onTouchStart={handle} onTouchMove={handle} onTouchEnd={reset}
      style={{ width: 110, height: 110, borderRadius: "50%", background: "rgba(0,0,0,0.4)", border: "1px solid rgba(56,225,255,0.3)", touchAction: "none" }}>
      <div style={{ position: "absolute", left: "50%", top: "50%", width: 44, height: 44, marginLeft: -22, marginTop: -22, transform: `translate(${knob.x}px,${knob.y}px)`, borderRadius: "50%", background: "rgba(56,225,255,0.5)" }} />
    </div>
  );
}
